import React from 'react'
import copy from 'copy-to-clipboard'
import styled from 'styled-components'
import * as zod from 'zod'
import { useAmplitudeEmbedCodeMutation } from '@mote/api'
import { CopyIcon, SearchIcon, TickFilledIcon } from '@mote/assets/icons'
import { useDebouncedCallback, useQueryParams } from '@mote/helpers'
import { isNumber } from '@mote/helpers/assertion'
import { CLIPBOARD_ANIMATION_TIMEOUT } from '@mote/helpers/constants'
import { useCustomForm } from '@mote/helpers/hooks/business'
import {
  Alert,
  Block,
  BlockGroup,
  Button,
  Dialog,
  H4,
  Input,
  Spinner,
  Switch,
  Tooltip
} from '@mote/ui'
import { FormInput } from '@mote/ui'
import { AdminLayout } from '../../layouts/Admin/admin.layout'
import { Content } from '../../layouts/shared'
import { AdminSchoolsPaginator } from './AdminSchoolsPaginator'
import {
  enableFallbackUnlimitedDomain,
  enableForcedMinorUnlimitedDomain
} from './adminHelpers'
import {
  AdminSchoolsProvider,
  DEFAULT_STATE,
  useAdminSchools
} from './adminSchools.context'

const MODAL_FORM_SCHEMA = zod.object({
  embedCode: zod.string({
    invalid_type_error: 'Please enter at least one character'
  })
})

const ICONS = {
  COPY: <CopyIcon color='currentColor' width={14} height={14} />,
  TICK: (
    <TickFilledIcon color={{ background: '#57C96A' }} width={14} height={14} />
  )
}

const AdminSchoolsRoot = () => {
  const {
    state,
    searchSchools,
    updateState,
    refreshSchools,
    refreshSchoolsPaginated
  } = useAdminSchools()
  const { queryParams, setQueryParams } = useQueryParams()

  const debouncedSearchSchools = useDebouncedCallback(searchTerm => {
    updateState({ searchTerm })
    searchSchools(searchTerm)
  }, 800)

  const handleSearchTermChange = React.useCallback(
    event => {
      const { value: searchTerm } = event.target
      debouncedSearchSchools(searchTerm)
    },
    [debouncedSearchSchools]
  )

  const handleToggleAll = React.useCallback(() => {
    const showAll = !state.showAll
    const institutions = showAll
      ? state.allInstitutions
      : state.paginatedInstitutions
    const listSchools = showAll ? refreshSchools : refreshSchoolsPaginated

    setQueryParams({ showAll })

    if (institutions.length) {
      updateState({ institutions })
    } else {
      listSchools()
    }

    updateState({ showAll, searchTerm: '' })
  }, [
    state.showAll,
    updateState,
    refreshSchools,
    refreshSchoolsPaginated,
    state.allInstitutions,
    state.paginatedInstitutions,
    queryParams
  ])

  const schools = React.useMemo(() => {
    return (
      <Block gap={40} spreadX>
        <TableContainer>
          {state.institutions.length === 0 && !state.loading && (
            <p align='center'>No schools found</p>
          )}
          {state.institutions.length > 0 && (
            <Table className='table truncate-school-name'>
              <thead className='thead-light'>
                <tr>
                  <th scope='col'>Institution id</th>
                  <th className='school-name' scope='col'>
                    Analytics
                  </th>
                  <th className='school-name' scope='col'>
                    Full school name
                  </th>
                  <th className='school-name' scope='col'>
                    Owner name
                  </th>
                  <th scope='col'>Owner email</th>
                  <th className='school-name' scope='col'>
                    Domains
                  </th>
                  <th className='school-name' scope='col'>
                    Product
                  </th>
                  <th className='school-name' scope='col'>
                    Unused paid licenses?
                  </th>
                  <th className='school-name' scope='col'>
                    Seats used vs paid
                  </th>

                  <th className='school-name' scope='col'>
                    Subscription valid until
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.institutions.map((s, index) => (
                  <SchoolEntry key={s.institutionId} school={s} index={index} />
                ))}
              </tbody>
            </Table>
          )}
        </TableContainer>
        {!state.showAll && <AdminSchoolsPaginator />}
      </Block>
    )
  }, [state.institutions, state.showAll])

  return (
    <AdminLayout
      title={
        <HeaderContainer variant='wide'>
          <Block
            layout='horizontal'
            align='center'
            justify='space-between'
            gap={24}
            spreadX
          >
            <H4 style={{ whiteSpace: 'nowrap' }}>Schools Management</H4>

            <Block
              layout='horizontal'
              gap={12}
              align='center'
              justify='flex-end'
              spreadX
            >
              <Input
                placeholder='Enter institution details'
                value={state.searchTerm}
                onChange={handleSearchTermChange}
                contentLeft={<SearchIcon color='currentColor' />}
                disabled={state.loading}
                maxWidth={300}
                fullWidth
                isClearable
              />
              <Button
                disabled={state.loading}
                text={state.showAll ? 'Hide all' : 'Show all'}
                onClick={handleToggleAll}
              />
            </Block>
          </Block>
          {!state.showAll && <AdminSchoolsPaginator />}
        </HeaderContainer>
      }
      containerVariant='wide'
    >
      {state.mode === 'list' && (
        <>
          {state.loading && <Spinner radius={90} color='#AC0AE8' />}
          {state.error && (
            <p align='center' style={{ color: 'red' }}>
              {state.error}
            </p>
          )}
          {!state.loading && !state.error && schools}
        </>
      )}
      {state.embedModal.open && (
        <EmbedModal
          content={state.embedModal.content}
          onClose={() =>
            updateState({
              embedModal: DEFAULT_STATE.embedModal
            })
          }
        />
      )}
    </AdminLayout>
  )
}

const SchoolEntry = ({ school: s, index }) => {
  const { state, updateState } = useAdminSchools()
  const l = s.currentLicense
  const [copyToClipboardIcon, setCopyToClipboardIcon] = React.useState(
    ICONS.COPY
  )
  const [isCopying, setIsCopying] = React.useState(false)
  const copyTimeoutRef = React.useRef(null)

  async function handleCopySchoolIdToClipboard() {
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current)
    }
    setIsCopying(true)

    try {
      await navigator.clipboard.writeText(s.institutionId)
    } catch {
      copy(s.institutionId)
    }

    setIsCopying(false)
    setCopyToClipboardIcon(ICONS.TICK)

    copyTimeoutRef.current = setTimeout(
      () => setCopyToClipboardIcon(ICONS.COPY),
      CLIPBOARD_ANIMATION_TIMEOUT
    )
  }

  return (
    <tr>
      <td>
        <Block layout='horizontal' gap={8} align='center' maxWidth={215}>
          <CopyButton
            aria-label='Copy school id'
            variant='ghost'
            icon={
              <Tooltip
                content={isCopying ? 'Copying..' : 'Copy id to clipboard'}
                direction='down-start'
              >
                {copyToClipboardIcon}
              </Tooltip>
            }
            loading={isCopying}
            onClick={handleCopySchoolIdToClipboard}
            style={{
              pointerEvents: copyToClipboardIcon === ICONS.TICK ? 'none' : null
            }}
          />
          <InstitutionLink
            variant='link'
            tag='a'
            href={'/account?iid=' + s.institutionId}
            text={s.institutionId}
          />
        </Block>
      </td>
      <td>
        <ButtonWithIcon
          variant='link'
          icon={s.analyticsEmbedCode ? ICONS.TICK : null}
          text={s.analyticsEmbedCode ? 'Edit' : 'Embed'}
          onClick={() =>
            updateState({
              embedModal: {
                open: !state.embedModal.open,
                content: s.analyticsEmbedCode || '',
                institutionId: s.institutionId,
                institutionIndex: index
              }
            })
          }
        />
      </td>
      <td>{<TruncatedText>{s.fullName}</TruncatedText>}</td>
      <td>{s.accountOwnerName}</td>
      <td>{<TruncatedText>{s.accountOwnerEmail}</TruncatedText>}</td>
      <td style={{ maxWidth: 335 }}>{<SchoolDomain school={s} />}</td>
      <td style={{ textTransform: 'capitalize', textAlign: 'center' }}>
        {l.highestProductEnum}
      </td>
      <td style={{ textAlign: 'center' }}>
        {l.canAddHighestProductEnumUsers ? 'Y' : 'N'}
      </td>
      <td style={{ textAlign: 'center' }}>
        {`${
          isNumber(l.totalPaidSeats) ? l.totalPaidSeats - l.unusedPaidSeats : 0
        }/${l.totalPaidSeats || 0}`}
      </td>
      <td>
        {s.subscriptionValidUntil
          ? new Date(s.subscriptionValidUntil).toLocaleDateString('en-gb')
          : 'N/A'}
      </td>
    </tr>
  )
}

const SchoolDomain = ({ school: s }) => {
  const { state, updateState, lightRefreshSchools } = useAdminSchools()

  const unlimitedOn = s.enableFallbackUnlimitedDomain
  const unlimitedLoading = `unlimited${s.institutionId}`

  const unlimitedForcedMinorOn = s.enableForcedMinorUnlimitedDomain
  const unlimitedForcedMinorLoading = `unlimitedForcedMinor${s.institutionId}`

  return (
    <BlockGroup gap={24}>
      <Block gap={8}>
        Unlimited + Force minor
        <Switch
          checked={unlimitedForcedMinorOn}
          onChange={() => {
            updateState({ [unlimitedForcedMinorLoading]: true })
            enableForcedMinorUnlimitedDomain(
              s.institutionId,
              !unlimitedForcedMinorOn,
              () => lightRefreshSchools(unlimitedForcedMinorLoading),
              () => {}
            )
          }}
          label={state[unlimitedForcedMinorLoading] ? 'Updating ...' : null}
        />
      </Block>

      <Block gap={8}>
        Unlimited (Unspecified age groups)
        <Switch
          checked={unlimitedOn}
          onChange={() => {
            updateState({ [unlimitedLoading]: true })
            enableFallbackUnlimitedDomain(
              s.institutionId,
              !unlimitedOn,
              () => lightRefreshSchools(unlimitedLoading),
              () => {}
            )
          }}
          label={state[unlimitedLoading] ? 'Updating ...' : null}
        />
      </Block>
    </BlockGroup>
  )
}

const EmbedModal = ({ onClose, onSave }) => {
  const { state } = useAdminSchools()
  const {
    mutate: saveEmbedCode,
    loading,
    flush,
    error: saveError
  } = useAmplitudeEmbedCodeMutation()
  const { control, handleSubmit, getValues } = useCustomForm({
    schema: MODAL_FORM_SCHEMA,
    defaultValues: { embedCode: state.embedModal.content }
  })
  const [error, setError] = React.useState(null)
  const [saving, setSaving] = React.useState(false)
  const textAreaRef = React.useRef(null)

  React.useEffect(() => {
    if (!saveError) return

    const errorCode = saveError.response?.status || saveError.name

    switch (errorCode) {
      case 418: {
        setError(
          'The embed code is invalid. Only Amplitude Analytics embed codes are supported'
        )
        break
      }
      default: {
        setError('Something went wrong. Please try again.')
      }
    }
  }, [saveError])

  const handleSaveCode = React.useCallback(async () => {
    const formValues = getValues()
    const embedCode = formValues.embedCode
      .replaceAll('“', '"')
      .replaceAll('”', '"')

    if (embedCode === state.embedModal.content) return onClose()

    setSaving(true)
    setError(null)
    flush()

    try {
      const response = await saveEmbedCode({
        variables: {
          institutionId: state.embedModal.institutionId,
          analyticsEmbedCode: embedCode
        }
      })
      if (!response || response.error) {
        setSaving(false)
        return
      }

      const { institutionIndex } = state.embedModal

      if (institutionIndex >= 0)
        state.institutions[institutionIndex].analyticsEmbedCode = embedCode

      onClose()
    } catch (error) {
      setSaving(false)
      setError('Something went wrong. Please try again.')
    }
  }, [state])

  return (
    <Dialog
      id='embed-analytics'
      isOpen
      onClose={onClose}
      initialFocusRef={textAreaRef}
      maxWidth={475}
    >
      <Dialog.Header
        style={{
          fontSize: 15
        }}
      >
        Embed analytics code
      </Dialog.Header>
      <Dialog.Content as='form' onSubmit={handleSubmit(handleSaveCode)}>
        {error && (
          <Alert variant='error' message={error} style={{ marginBottom: 12 }} />
        )}
        <FormInput
          ref={textAreaRef}
          name='embedCode'
          placeholder='Enter embed code from amplitude here'
          type='textarea'
          control={control}
          inputProps={{ rows: 10, maxWidth: '100%' }}
          required
        />
      </Dialog.Content>
      <Dialog.Footer>
        <Button onClick={onClose} text='Cancel' variant='soft' autoWidth />
        <Button
          type='submit'
          text='Save'
          form='mote-dialog--content-embed-analytics'
          loadingText='Saving ...'
          loading={saving || loading}
          autoWidth
        />
      </Dialog.Footer>
    </Dialog>
  )
}

const AdminSchools = () => {
  return (
    <AdminSchoolsProvider>
      <AdminSchoolsRoot />
    </AdminSchoolsProvider>
  )
}

export const AdminSchoolsScreen = AdminSchools

const TableContainer = styled.div`
  width: 100%;
  overflow: hidden;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;

  th {
    text-align: left;
    padding: 22px 16px;
    font-weight: 400;
    font-size: 15px;
    line-height: 100%;
    border-top: 1px solid #d1d1e0;
    background-color: #f9f9fb;
  }

  td {
    padding: 32px 16px;
    border-top: 1px solid #d1d1e0;
    text-align: left;
    font-size: 15px;
    line-height: 100%;
    color: #646f7b;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 215px;
    white-space: nowrap;
  }

  thead th {
    border: 0;
    font-weight: 500;
    white-space: nowrap;
    vertical-align: bottom;
  }

  tbody tr {
    &:hover {
      background: rgba(213, 212, 218, 0.25);
    }
  }
`

const TruncatedText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 120%;
  display: block;
`

const InstitutionLink = styled(Button)`
  width: 100%;
  max-width: calc(215px - 64px);

  & .button-text {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const ButtonWithIcon = styled(Button)`
  & span.button-icon {
    width: 14px;
    height: 14px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`

const CopyButton = styled(ButtonWithIcon)`
  height: 24px;
  width: 24px;
  border-radius: 50%;
`

const HeaderContainer = styled(Content)`
  display: flex;
  flex-direction: column;
  align-items: initial;
  justify-content: center;
  padding: 0 40px;
  gap: 72px;

  @media (max-width: 639px) {
    padding: 0 24px;
  }
`
